import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { SimpleSelect } from "components/CustomSelectFields";
import { CategoryScale, LinearScale, BarElement, Chart as ChartJS, Title, Tooltip } from "chart.js";
import { Divider, CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";

import CenterFlexBox from "components/CenterFlexBox";
import Tabs from "components/Tabs";
import { getStudentRecentInterviewScoreAndAttempts, getStudentInterviewAttempts } from "services/analyticsV2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const useStyles = makeStyles((theme) => ({
  tooltip: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #F0F0F0",
    boxShadow: "5px 3px 13px 0px #4747491A",
    padding: theme.spacing(1,4),
    transform: "all 200ms",
    pointerEvents: "none",
    width: 300,
  },
  tooltipHeading: {
    ...theme.typography['body01-bold'],
    textTransform: "capitalize",
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  tooltipData: {
    padding: theme.spacing(1,5),
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tooltipDataValue: {
    ...theme.typography['body01-semiBold'],
  },
  tooltipDataLabel: {
    ...theme.typography['body01-medium'],
    color: theme.palette.neutral['clr-800']
  },
}));

const getDefaultSection = (activeTab) => (activeTab === 'interviewWizard' ? 'Interview' : 'Mock');

const getXAxisTitle = (type, section) => {
  switch (type) {
    case 'verbal_mock':
      return section === 'Mock' ? 'Next Round Mock' : 'Next Round Attempts';
    case 'coding_mock':
      return section === 'Mock' ? 'Coding Mock' : 'Coding Attempts';
    case 'verbal_iw':
      return section === 'Interview' ? 'Next Round Interview' : 'Next Round Attempts';
    case 'coding_iw':
      return section === 'Interview' ? 'Coding Interview' : 'Coding Attempts';
    case 'assessment':
      return section === 'Mock' ? 'Assessment' : 'Assessment Attempts';
    default:
      return section === 'Mock' ? 'Next Round Mock' : 'Next Round Mock Attempts';
  }
};

const getAnalyticsLabels = (type, section) => {
  switch (type) {
    case 'verbal_mock':
      return {
        title: 'Next Round Mock Analytics',
        subtitle: 'Analyze student performance in Next Round Mocks and individual attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
    case 'coding_mock':
      return {
        title: 'Coding Mocks & Contests Analytics',
        subtitle: 'Analyze student performance in Coding Mocks, Contests, and individual attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
    case 'verbal_iw':
      return {
        title: 'Next Round Interview Analytics',
        subtitle: 'View detailed scoring for next round interviews and its attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
    case 'coding_iw':
      return {
        title: 'Coding Interview Analytics',
        subtitle: 'Get detailed insights for coding interviews and its attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
    case 'assessment':
      return {
        title: 'Assessment Analytics',
        subtitle: 'Analyze student performance in assessments and individual attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
    default:
      return {
        title: 'Next Round Mock Analytics',
        subtitle: 'Analyze student performance in Next Round Mocks and individual attempts',
        xAxisTitle: getXAxisTitle(type, section)
      };
  }
};

const buildLabels = (type, data, section) => {
  if (type === 'verbal_mock' || type === 'verbal_iw') {
    return data.map((item, index) => (
      section === 'Attempt'
        ? `Attempt - ${index + 1}`
        : (item.name || item.settings?.name || item.interviewTemplate || `Item ${index + 1}`)
    ));
  }
  if (type === 'coding_mock' || type === 'coding_iw') {
    return data.map((item, index) => (
      section === 'Attempt'
        ? `Attempt - ${index + 1}`
        : (item.challenge || item.settings?.name || item.name || item.title || `Item ${index + 1}`)
    ));
  }
  // Default verbal-like labels
  return data.map((item, index) => (
    section === 'Attempt'
      ? `Attempt - ${index + 1}`
      : (item.name || item.settings?.name || item.interviewTemplate || `Item ${index + 1}`)
  ));
};

const buildDatasets = (type, data, section) => {
  const datasets = [];
  if (type === 'verbal_mock' || type === 'verbal_iw') {
    datasets.push({
      label: 'Speech',
      data: data.map(item => (section === 'Attempt'
        ? (item.analysis?.communication?.ratings?.OVERALL || 0)
        : (isNaN(Number(item.overallComm)) ? 0 : Number(item.overallComm))
      )),
      backgroundColor: '#B8ACF6',
      hoverBackgroundColor: '#8270DB',
    });
    datasets.push({
      label: 'Content',
      data: data.map(item => (section === 'Attempt'
        ? (item.analysis?.content?.ratings?.OVERALL || 0)
        : (isNaN(Number(item.overallContent)) ? 0 : Number(item.overallContent))
      )),
      backgroundColor: '#9DE4F2',
      hoverBackgroundColor: '#52B8CC',
    });
    return datasets;
  }
  if (type === 'coding_mock' || type === 'coding_iw') {
    if (section === 'Attempt') {
      datasets.push({ label: 'Coding', data: data.map(item => item.coding?.overall ?? 0), backgroundColor: '#6E5DC6' });
      datasets.push({ label: 'Core Concepts', data: data.map(item => item.coreConcepts?.overall ?? 0), backgroundColor: '#52B8CC' });
      datasets.push({ label: 'Critical Thinking', data: data.map(item => item.criticalThinking?.overall ?? 0), backgroundColor: '#1151A6' });
      datasets.push({ label: 'Communication', data: data.map(item => item.content?.overall ?? 0), backgroundColor: '#737DA8' });
    } else {
      // New response also includes aggregated 4C: coding, coreConcepts, criticalThinking, content, speech
      datasets.push({ label: 'Coding', data: data.map(item => Number(item.coding) || 0), backgroundColor: '#6E5DC6' });
      datasets.push({ label: 'Core Concepts', data: data.map(item => Number(item.coreConcepts) || 0), backgroundColor: '#52B8CC' });
      datasets.push({ label: 'Critical Thinking', data: data.map(item => Number(item.criticalThinking) || 0), backgroundColor: '#1151A6' });
      datasets.push({ label: 'Content', data: data.map(item => Number(item.content) || 0), backgroundColor: '#737DA8' });
      datasets.push({ label: 'Speech', data: data.map(item => Number(item.speech) || 0), backgroundColor: '#9DB2F2' });
    }
    return datasets;
  }
  return datasets;
};

const createExternalTooltip = (classes, filteredData, section, interviewVariantType) => (context) => {
  const { chart } = context;
  let tooltipEl = document.getElementById('chartjs-tooltip-student');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-tooltip-student';
    tooltipEl.classList.add(classes.tooltip);
    chart.canvas.style.position = 'relative';
    chart.canvas.parentNode.appendChild(tooltipEl);
  }
  tooltipEl.innerHTML = '';

  const tooltipModel = context.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  tooltipEl.classList.remove('above', 'below', 'no-transform');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  const dataIndex = tooltipModel.dataPoints?.[0].dataIndex;
  const currData = filteredData[dataIndex] || {};

  const heading = document.createElement('p');
  heading.className = classes.tooltipHeading;
  const templateName = section === 'Attempt'
    ? `Attempt - ${dataIndex + 1}`
    : (currData.name || currData.settings?.name || currData.templateName || currData.title || `Item ${dataIndex + 1}`);
  heading.innerHTML = templateName;
  tooltipEl.appendChild(heading);

  const dataContainer = document.createElement('div');
  dataContainer.style.width = '100%';
  let tooltipContent = '';

  if (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') {
    if (section === 'Attempt') {
      const codingValue = currData.coding?.overall ?? 0;
      const coreConceptsValue = currData.coreConcepts?.overall ?? 0;
      const criticalThinkingValue = currData.criticalThinking?.overall ?? 0;
      const contentValue = currData.content?.overall ?? 0;
      const codingDisp = Math.round(Number(codingValue) || 0);
      const coreConceptsDisp = Math.round(Number(coreConceptsValue) || 0);
      const criticalThinkingDisp = Math.round(Number(criticalThinkingValue) || 0);
      const contentDisp = Math.round(Number(contentValue) || 0);
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Coding</div>
          <div class='${classes.tooltipDataValue}'>${codingDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Core Concepts</div>
          <div class='${classes.tooltipDataValue}'>${coreConceptsDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Critical Thinking</div>
          <div class='${classes.tooltipDataValue}'>${criticalThinkingDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Communication</div>
          <div class='${classes.tooltipDataValue}'>${contentDisp}/5</div>
        </div>`;
    } else {
      const codingValue = Number(currData.coding) || 0;
      const coreConceptsValue = Number(currData.coreConcepts) || 0;
      const criticalThinkingValue = Number(currData.criticalThinking) || 0;
      const contentValue = Number(currData.content) || 0;
      const speechValue = Number(currData.speech) || 0;
      const codingDisp = Math.round(codingValue);
      const coreConceptsDisp = Math.round(coreConceptsValue);
      const criticalThinkingDisp = Math.round(criticalThinkingValue);
      const contentDisp = Math.round(contentValue);
      const speechDisp = Math.round(speechValue);
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Coding</div>
          <div class='${classes.tooltipDataValue}'>${codingDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Core Concepts</div>
          <div class='${classes.tooltipDataValue}'>${coreConceptsDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Critical Thinking</div>
          <div class='${classes.tooltipDataValue}'>${criticalThinkingDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Content</div>
          <div class='${classes.tooltipDataValue}'>${contentDisp}/5</div>
        </div>`;
      tooltipContent += `
        <div class='${classes.tooltipData}'>
          <div class='${classes.tooltipDataLabel}'>Speech</div>
          <div class='${classes.tooltipDataValue}'>${speechDisp}/5</div>
        </div>`;
    }
  } else {
    let speechValue, contentValue;
    if (section === 'Attempt') {
      speechValue = currData.analysis?.communication?.ratings?.OVERALL || 0;
      contentValue = currData.analysis?.content?.ratings?.OVERALL || 0;
    } else {
      speechValue = isNaN(Number(currData.overallComm)) ? 0 : Number(currData.overallComm);
      contentValue = isNaN(Number(currData.overallContent)) ? 0 : Number(currData.overallContent);
    }
    tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Speech</div>
        <div class='${classes.tooltipDataValue}'>${speechValue}</div>
      </div>`;
    tooltipContent += `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Content</div>
        <div class='${classes.tooltipDataValue}'>${contentValue}</div>
      </div>`;
  }

  dataContainer.innerHTML = tooltipContent;
  tooltipEl.appendChild(dataContainer);

  const { offsetLeft: positionX, offsetTop: positionY } = context.chart.canvas;
  tooltipEl.style.opacity = 1;
  tooltipEl.style.zIndex = 10000000000;
  tooltipEl.style.left = positionX + tooltipModel.caretX - 250 + 'px';
  tooltipEl.style.top = positionY + tooltipModel.caretY - 140 + 'px';
};

function StudentAnalyticsEmptyState({ analyticsLabels }) {
  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <CenterFlexBox justifyContent="space-between">
        <Box display='flex' flexDirection='column' gap='4px' width='80%'>
          <Typography variant="h4-medium">
            {analyticsLabels.title}
          </Typography>
          <Typography variant="h6-medium" color='neutral.clr-600'>
            {analyticsLabels.subtitle}
          </Typography>
        </Box>
      </CenterFlexBox>
      <Divider sx={{borderColor: '#CCD4DE'}}/>
      
      <Box 
        mt={2} 
        className="chart-container" 
        borderRadius={2} 
        border="1px solid #E0E0E0" 
        bgcolor="#fff" 
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={300}
      >
        <Box mb={2}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <rect width="64" height="64" rx="12" fill="#F5F9FF"/>
            <path d="M20 24h24v16H20V24z" stroke="#CCD4DE" strokeWidth="2" fill="none"/>
            <path d="M24 28h4v8h-4V28z" fill="#E3EDFB"/>
            <path d="M30 32h4v4h-4v-4z" fill="#E3EDFB"/>
            <path d="M36 30h4v6h-4v-6z" fill="#E3EDFB"/>
          </svg>
        </Box>
        <Typography variant="h6-medium" color="neutral.clr-900" mb={1} textAlign="center">
          No Data Available
        </Typography>
      </Box>
    </Box>
  );
}

function StudentAnalytics({ 
  studentId, 
  batchId, 
  interviewVariantType, 
  activeTab, 
  interviewType,
  analyticsSection,
  setAnalyticsSection,
  selectedMock,
  setSelectedMock
}) {
  const classes = useStyles();

  const [recentData, setRecentData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [attemptData, setAttemptData] = React.useState([]);
  const [attemptLoading, setAttemptLoading] = React.useState(false);
  const [attemptError, setAttemptError] = React.useState(null);

  React.useEffect(() => {
    const defaultSection = getDefaultSection(activeTab);
    if (!analyticsSection || analyticsSection === '') {
      setAnalyticsSection(defaultSection);
    }
  }, [activeTab, setAnalyticsSection]);

  React.useEffect(() => {
    const defaultSection = getDefaultSection(activeTab);
    if (!analyticsSection) {
      setAnalyticsSection(defaultSection);
    }
  }, []);

  const section = React.useMemo(() => (analyticsSection || getDefaultSection(activeTab)), [analyticsSection, activeTab]);

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    getStudentRecentInterviewScoreAndAttempts({
      studentId,
      interviewVariantType,
      batchId: activeTab !== 'interviewWizard' ? batchId : null,
      page: 1,
      pageSize: 10,
    })
      .then(res => {
        const arr = res?.performance || res?.data || [];
        setRecentData(arr);
        if (arr.length > 0) {
          setSelectedMock(arr[0]._id);
        }
      })
      .catch(err => setError(err?.message || 'Failed to fetch data'))
      .finally(() => setLoading(false));
  }, [studentId, interviewVariantType, batchId, activeTab]);

  React.useEffect(() => {
    if (section === 'Attempt' && selectedMock) {
      setAttemptLoading(true);
      setAttemptError(null);
      getStudentInterviewAttempts({
        studentId,
        interviewVariantType,
        interviewId: selectedMock,
        pageSize: 10,
        pageNumber: 1,
        orderBy: 'title',
        order: 'asc',
      })
        .then(res => {
          const arr = res?.data || [];
          setAttemptData(arr);
        })
        .catch(err => setAttemptError(err?.message || 'Failed to fetch attempt data'))
        .finally(() => setAttemptLoading(false));
    }
  }, [section, selectedMock, studentId, interviewVariantType]);

  const ToggleOptions = React.useMemo(() => (
    activeTab === 'interviewWizard'
      ? [
          { _id: 'Interview', label: 'Interview' },
          { _id: 'Attempt', label: 'Attempt' },
        ]
      : [
          { _id: 'Mock', label: 'Mock' },
          { _id: 'Attempt', label: 'Attempt' },
        ]
  ), [activeTab]);

  const handleToggleChange = (toggleId) => {
    setAnalyticsSection(toggleId);
  };

  let filteredData = section === 'Attempt' ? attemptData : recentData;

  const mockOptions = React.useMemo(() => recentData.map(item => ({
    _id: item._id,
    name: item.name || item.settings?.name || item.title || 'Untitled',
  })), [recentData]);

  const handleMockChange = (e) => setSelectedMock(e.target.value);

  const analyticsLabels = React.useMemo(() => getAnalyticsLabels(interviewVariantType, section), [interviewVariantType, section]);

  const externalTooltip = React.useMemo(
    () => createExternalTooltip(classes, filteredData, section, interviewVariantType),
    [classes, filteredData, section, interviewVariantType]
  );

  const labels = React.useMemo(() => buildLabels(interviewVariantType, filteredData, section), [interviewVariantType, filteredData, section]);
  const datasets = React.useMemo(() => buildDatasets(interviewVariantType, filteredData, section), [interviewVariantType, filteredData, section]);

  const chartOptions = React.useMemo(() => ({
    responsive: true,
    aspectRatio: 3,
    maintainAspectRatio: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: false,
        position: "average",
        external: externalTooltip,
      },
    },
    onClick: (e) => {
      const tooltipEl = document.getElementById("chartjs-tooltip-student");
      if (tooltipEl) {
        tooltipEl.style.opacity = 0;
      }
    },
    scales: {
      y: {
        min: 0,
        max: (interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw') ? 5 : 100,
        title: { display: true, text: "Score range" }
      },
      x: { 
        title: { display: true, text: analyticsLabels.xAxisTitle },
        display: true,
        ticks: {
          callback: function(value, index, ticks) {
            const label = this.getLabelForValue(value);
            return label.length > 10 ? label.substr(0, 10) + '...' : label;
          },
          maxRotation: 45,
          minRotation: 0,
        }
      },
    },
  }), [externalTooltip, interviewVariantType, analyticsLabels.xAxisTitle]);

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <CenterFlexBox justifyContent="space-between">
        <Box display='flex' flexDirection='column' gap='4px' width='80%'>
          <Typography variant="h4-medium">
            {analyticsLabels.title}
          </Typography>
          <Typography variant="h6-medium" color='neutral.clr-600'>
            {analyticsLabels.subtitle}
          </Typography>
        </Box>
      </CenterFlexBox>
      <Divider sx={{borderColor: '#CCD4DE'}}/>  
      <Box display="flex" flexDirection="column"  gap={4} mb={2}>
        <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body01-bold" mb={0.5}>View by</Typography>
                         <Tabs
               key={section}
               options={ToggleOptions}
               value={section || (activeTab === 'interviewWizard' ? 'Interview' : 'Mock')}
               onChange={handleToggleChange}
               width='180px'
             />
            </Box>
        {section === 'Attempt' && (
          <Box minWidth={260}>
            <SimpleSelect
              label={
                activeTab === 'interviewWizard' 
                  ? (interviewVariantType === 'coding_iw' ? 'Select Coding Challenge' : 'Select Verbal Interview')
                  : (interviewVariantType === 'coding_mock' ? 'Select Coding Challenge' : 'Select Verbal Mock')
              }
              options={mockOptions}
              value={selectedMock}
              onChange={handleMockChange}
            />
          </Box>
        )}
      </Box>
      {(loading || (section === 'Attempt' && attemptLoading)) ? (
        <Box mt={2} className="chart-container" borderRadius={2} border="1px solid #E0E0E0" bgcolor="#fff" p={2}>
          <CenterFlexBox><CircularProgress /></CenterFlexBox>
        </Box>
      ) : (error || (section === 'Attempt' && attemptError)) ? (
        <StudentAnalyticsEmptyState analyticsLabels={analyticsLabels} />
      ) : (
        <Box mt={2} className="chart-container" borderRadius={2} border="1px solid #E0E0E0" bgcolor="#fff" p={2}>
          <Bar
            options={chartOptions}
            data={{ labels, datasets }}
            style={{ maxHeight: 300 }}
          />
        </Box>
      )}
    </Box>
  );
}

export default StudentAnalytics;  